import {useCallback, useEffect, useState} from 'react'
import {useRouter} from 'next/router'
import {getAllPetNames} from '@/services/pet-names/client'

interface NameFilterHookResult {
	filterPetNames: (letterFrom: string, letterTo: string) => void
	filterBySpecie: (specie: string) => void
	loadMoreNames: (trait: string) => void
	loading: boolean
	loadMore: boolean
	showLoadMoreText: boolean
	selected: string
	names: string[]
	displayAllNames: () => void
}

interface PetNameResponse {
	names: string[]
}

type ApiResponse = (string | PetNameResponse)[]

export function useNameFilter(): NameFilterHookResult {
	const {locale = ''} = useRouter()
	const [petResultsList, setPetResultsList] = useState<string[]>([])
	const [loading, setLoading] = useState(false)
	const [loadMore, setLoadMore] = useState(false)
	const [showLoadMoreText, setShowLoadMoreText] = useState(true)
	const [selected, setSelected] = useState<string>()
	const [selectedLetters, setSelectedLetters] = useState(['A', 'B', 'C', 'D'])

	const fetchAllPetNames = useCallback(async () => {
		setLoading(true)

		if (petResultsList.length > 0) {
			setLoading(false)
			return
		}

		try {
			const response = await getAllPetNames({locale})

			setPetResultsList(response)
		} catch (e) {
			setPetResultsList([])
		} finally {
			setLoading(false)
		}
	}, [locale])

	async function fetchSpeciesName(specie: string): Promise<void> {
		setLoading(true)

		if (petResultsList.length > 0) {
			setLoading(false)
			return
		}
		try {
			const response = (await getAllPetNames({
				type: specie,
				locale,
			})) as ApiResponse

			setPetResultsList(response as string[])
		} catch (e) {
			setPetResultsList([])
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		void fetchAllPetNames()
	}, [fetchAllPetNames])

	const filterPetNames = (letterFrom: string, letterTo: string): void => {
		setLoadMore(false)
		setShowLoadMoreText(true)
		const alphabet = [
			'A',
			'B',
			'C',
			'D',
			'E',
			'F',
			'G',
			'H',
			'I',
			'J',
			'K',
			'L',
			'M',
			'N',
			'O',
			'P',
			'Q',
			'R',
			'S',
			'T',
			'U',
			'V',
			'W',
			'X',
			'Y',
			'Z',
		]
		const indexOfFrom = alphabet.indexOf(letterFrom)
		const indexOfTo = alphabet.indexOf(letterTo)
		setSelectedLetters(alphabet.slice(indexOfFrom, indexOfTo + 1))
	}

	const filterBySpecie = (specie: string): void => {
		setSelected(specie)
		void fetchSpeciesName(specie)
	}

	const loadMoreNames = (trait: string): void => {
		setSelected(trait)
		void fetchAllPetNames()
	}

	const displayAllNames = (): void => {
		setLoadMore(true)
		setShowLoadMoreText(false)
	}

	const filteredNames = petResultsList.filter((name) =>
		selectedLetters.includes(name.charAt(0).toUpperCase())
	)

	return {
		filterPetNames,
		filterBySpecie,
		loadMoreNames,
		loading,
		loadMore,
		showLoadMoreText,
		selected: selected ?? '',
		names: filteredNames,
		displayAllNames,
	}
}
